/**
 * AppLoaderGate — the top-of-tree loader host. Paints a SINGLE
 * ``<FullscreenLoader />`` React element while ``useIsAppLoading`` is true,
 * and always renders ``children`` underneath. Children mount from the very
 * first render so their ``useEffect``s (which raise/lower the loader flags)
 * fire — the loader is a purely visual overlay, not a structural gate.
 *
 * Fixes the boot flicker where AuthContext and AppRoutes each mounted their
 * own FullscreenLoader at different tree positions and the video restarted on
 * the handoff. Here the loader's React element lives at one position, so its
 * <video> DOM node persists across every flag transition — auth resolves,
 * Suspense's chunk loader mounts, the flag stays raised, the same loader keeps
 * painting. When the last flag lowers, the loader unmounts once.
 */
import type { ReactNode } from 'react';
import FullscreenLoader from '@components/FullscreenLoader';
import { useIsAppLoading } from '@stores/appLoaderStore';

interface AppLoaderGateProps {
  children: ReactNode;
}

const AppLoaderGate = ({ children }: AppLoaderGateProps) => {
  const isLoading = useIsAppLoading();
  return (
    <>
      {isLoading && <FullscreenLoader />}
      {children}
    </>
  );
};

export default AppLoaderGate;
